import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { apiClient, formatApiError } from "@/lib/api";
import { formatGNF, STATUS_LABEL, STATUS_COLOR } from "@/utils/format";
import { toast } from "sonner";
import { Loader2, XCircle, ChefHat, Bike, PackageCheck, Clock } from "lucide-react";
import CancelOrderModal from "@/components/CancelOrderModal";
import { playAlertSound, vibrate } from "@/lib/pushNotifications";

const ETA_OPTIONS = [15, 25, 35, 45];

/** Restaurant → Orders panel.
 * Lifecycle: received → "Accepter" (+ ETA) → preparing → "Prête pour livraison" → ready
 * (auto-dispatch to drivers starts ONLY at "ready") → delivering → delivered.
 */
export default function RestaurantOrdersPanel() {
  const { t } = useTranslation();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});
  const [cancelId, setCancelId] = useState(null);
  const [etaPickerId, setEtaPickerId] = useState(null); // order awaiting ETA choice
  const [searchParams] = useSearchParams();
  const highlight = searchParams.get("highlight");
  // Deep link from « Voir la commande » (payouts) → scroll to and flash the order card
  useEffect(() => {
    if (!highlight || orders.length === 0) return;
    const t = setTimeout(() => {
      const el = document.getElementById(`resto-order-${highlight}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("deep-link-highlight");
        setTimeout(() => el.classList.remove("deep-link-highlight"), 3200);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [highlight, orders.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const refresh = useCallback(async () => {
    try {
      const { data } = await apiClient.get("/my/restaurant/orders");
      setOrders(data || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); const t = setInterval(refresh, 15000); return () => clearInterval(t); }, [refresh]);

  // In-app alert: sound + vibration + toast when a NEW order arrives (status "received")
  const seenReceivedRef = useRef(new Set());
  const firstLoadRef = useRef(true);
  useEffect(() => {
    const received = orders.filter((o) => o.status === "received").map((o) => o.order_id);
    if (firstLoadRef.current) {
      if (!loading) firstLoadRef.current = false;
      received.forEach((id) => seenReceivedRef.current.add(id));
      return;
    }
    const fresh = received.filter((id) => !seenReceivedRef.current.has(id));
    if (fresh.length > 0) {
      playAlertSound("priority");
      vibrate([300, 100, 300]);
      toast.warning(fresh.length > 1 ? `${fresh.length} nouvelles commandes reçues !` : t("rdash.kbNewOrder"), { duration: 8000 });
    }
    received.forEach((id) => seenReceivedRef.current.add(id));
  }, [orders, loading]);

  const setStatus = async (o, status, etaMinutes = null, successMsg = t("rdash.kbOrderUpdated")) => {
    setBusy((b) => ({ ...b, [o.order_id]: true }));
    try {
      const body = { status };
      if (etaMinutes) body.eta_minutes = etaMinutes;
      await apiClient.patch(`/orders/${o.order_id}/status`, body);
      toast.success(successMsg);
      setEtaPickerId(null);
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy((b) => ({ ...b, [o.order_id]: false }));
    }
  };

  if (loading) return <div className="text-mute py-8 text-center">Chargement des commandes…</div>;
  if (!orders.length) return (
    <div className="bg-white border border-sand rounded-2xl p-10 text-center text-mute" data-testid="orders-empty">
      Aucune commande pour l'instant.
    </div>
  );

  return (
    <div className="space-y-3" data-testid="restaurant-orders-panel">
      {orders.map((o) => (
        <div key={o.order_id} id={`resto-order-${o.order_id}`} data-testid={`resto-order-${o.order_id}`} className="bg-white border border-sand rounded-2xl p-4">
          <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
            <div>
              <div className="text-xs text-mute">#{o.order_id.slice(-6).toUpperCase()} · {new Date(o.created_at).toLocaleString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</div>
              {o.is_scheduled && (
                <div className="mt-1 inline-flex items-center gap-1.5 rounded-full bg-[#E67E22]/15 text-[#B35C00] px-2.5 py-0.5 text-[11px] font-bold" data-testid={`resto-scheduled-${o.order_id}`}>
                  {t("rdash.scheduledBadge")}
                </div>
              )}
              <div className="font-semibold text-sm">{o.address} · {o.zone}</div>
              <div className="text-xs text-mute">{o.phone}</div>
              {o.is_gift && (
                <div className="mt-1 inline-flex items-center gap-1.5 rounded-full bg-brand-yellow/25 px-2.5 py-0.5 text-[11px] font-bold" data-testid={`resto-gift-${o.order_id}`}>
                  🎁 Cadeau pour {o.gift_recipient_name}
                </div>
              )}
            </div>
            <div className="text-right">
              <div className="font-display text-lg font-semibold" data-testid={`resto-order-amount-${o.order_id}`}>{formatGNF(o.subtotal)}</div>
              <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold" style={{ backgroundColor: STATUS_COLOR[o.status] + "22", color: STATUS_COLOR[o.status] }}>
                {STATUS_LABEL[o.status]}
              </span>
            </div>
          </div>

          <div className="text-xs text-mute border-t border-sand pt-2 mb-3 space-y-0.5">
            {o.items?.map((it, idx) => (
              <div key={(it.dish_id || it.name) + idx}>
                <span className="text-ink">{it.quantity}× {it.name}</span>
                {it.options?.length > 0 && <span className="text-[#8A6D00]"> — {it.options.map((op) => op.name).join(", ")}</span>}
                {it.note && <span className="italic text-brand-red"> — « {it.note} »</span>}
              </div>
            ))}
            {o.notes && (
              <div className="mt-1 italic text-ink" data-testid={`rop-order-note-${o.order_id}`}>💬 « {o.notes} »</div>
            )}
          </div>

          {/* ETA quick-pick after clicking "Accepter" */}
          {etaPickerId === o.order_id && o.status === "received" && (
            <div className="rounded-xl bg-elevated p-3 mb-3" data-testid={`resto-eta-picker-${o.order_id}`}>
              <div className="text-xs font-semibold mb-2 flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {t("rdash.ropPrepTimeQ")}</div>
              <div className="flex gap-2 flex-wrap">
                {ETA_OPTIONS.map((m) => (
                  <button
                    key={m}
                    onClick={() => setStatus(o, "preparing", m, `Commande acceptée — prête dans ~${m} min`)}
                    disabled={busy[o.order_id]}
                    data-testid={`resto-eta-${m}-${o.order_id}`}
                    className="rounded-full bg-white border border-sand hover:border-brand-green px-4 py-2 text-xs font-semibold disabled:opacity-60"
                  >{m} min</button>
                ))}
                <button
                  onClick={() => setStatus(o, "preparing", null, t("rdash.ropAccepted"))}
                  disabled={busy[o.order_id]}
                  data-testid={`resto-eta-skip-${o.order_id}`}
                  className="rounded-full bg-white border border-sand hover:border-mute px-4 py-2 text-xs font-semibold text-mute disabled:opacity-60"
                >{t("rdash.ropSansEstimation")}</button>
              </div>
            </div>
          )}

          <div className="flex gap-2 flex-wrap">
            {o.status === "received" && etaPickerId !== o.order_id && (
              <button
                onClick={() => setEtaPickerId(o.order_id)}
                disabled={busy[o.order_id]}
                data-testid={`resto-advance-${o.order_id}`}
                className="rounded-full bg-brand-green hover:bg-[#007A4A] disabled:opacity-60 text-white px-4 py-2 text-xs font-semibold inline-flex items-center gap-1.5"
              >
                {busy[o.order_id] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ChefHat className="w-3.5 h-3.5" />}
                {t("rdash.ropAccepter")}
              </button>
            )}
            {o.status === "preparing" && (
              <>
                <button
                  onClick={() => setStatus(o, "ready", null, t("rdash.ropReadySearching"))}
                  disabled={busy[o.order_id]}
                  data-testid={`resto-ready-${o.order_id}`}
                  className="rounded-full bg-brand-red hover:bg-[#C81F2D] disabled:opacity-60 text-white px-4 py-2 text-xs font-semibold inline-flex items-center gap-1.5"
                >
                  {busy[o.order_id] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PackageCheck className="w-3.5 h-3.5" />}
                  {t("rdash.kbReadyForDelivery")}
                </button>
                {o.ready_estimate_at && (
                  <span className="rounded-full bg-elevated px-3 py-2 text-xs text-mute inline-flex items-center gap-1.5" data-testid={`resto-eta-badge-${o.order_id}`}>
                    <Clock className="w-3.5 h-3.5" /> {t("rdash.ropReadyAt")} {new Date(o.ready_estimate_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                  </span>
                )}
              </>
            )}
            {o.status === "ready" && (
              <span
                data-testid={`resto-dispatch-hint-${o.order_id}`}
                className="rounded-full bg-elevated px-4 py-2 text-xs font-semibold text-mute inline-flex items-center gap-2"
              >
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-red opacity-60" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-brand-red" />
                </span>
                Recherche d'un livreur en cours… (attribution automatique)
              </span>
            )}
            {o.status === "delivering" && o.driver_name && (
              <span
                data-testid={`resto-driver-${o.order_id}`}
                className="rounded-full bg-[#E7F6EE] px-4 py-2 text-xs font-semibold text-brand-green inline-flex items-center gap-1.5"
              >
                <Bike className="w-3.5 h-3.5" /> Livreur : {o.driver_name}
              </span>
            )}
            {["received", "preparing", "ready"].includes(o.status) && (
              <button
                onClick={() => setCancelId(o.order_id)}
                data-testid={`resto-cancel-${o.order_id}`}
                className="rounded-full border border-brand-red/40 text-brand-red hover:bg-brand-red hover:text-white transition-colors px-4 py-2 text-xs font-semibold inline-flex items-center gap-1.5"
              >
                <XCircle className="w-3.5 h-3.5" /> Annuler
              </button>
            )}
          </div>
        </div>
      ))}

      <CancelOrderModal
        open={!!cancelId}
        onClose={() => setCancelId(null)}
        orderId={cancelId}
        onCancelled={() => { setCancelId(null); refresh(); }}
      />
    </div>
  );
}
